import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@radix-ui/react-tooltip'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import { ButtonHTMLAttributes, useState } from 'react'

import Loader from './loader'
import Typography from './typography'

export type ActionButtonVariant =
  | 'primary'
  | 'danger'
  | 'grey'
  | 'lightPurple'
  | 'ghost'
  | 'success'
  | 'lightGreen'

type Props = {
  /**
   * Icon to display on the button
   */
  icon?: SVGFC

  /**
   * Is this the principal call to action on the page?
   */
  variant: ActionButtonVariant

  /**
   * class names for icon
   */
  className?: string

  /**
   * Is the button disabled
   *
   */
  disabled?: boolean

  /**
   * Text to display in the tooltip
   */
  tooltipText?: string

  /**
   * Optional loading state
   */
  isLoading?: boolean

  /**
   * Optional type for the button
   */
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type']

  /**
   * Optional size for the button
   */
  size?: 'x-small' | 'small' | 'default'

  /**
   * Optional click handler
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const mapVariantToColor: Record<ActionButtonVariant, TailwindColor> = {
  primary: tailwindColors.primary.DEFAULT,
  danger: tailwindColors.red.DEFAULT,
  grey: tailwindColors.grey.DEFAULT,
  lightPurple: tailwindColors.primary.DEFAULT,
  ghost: tailwindColors.primary.DEFAULT,
  success: tailwindColors.green.DEFAULT,
  lightGreen: tailwindColors.green.DEFAULT
}

const mapVariantToBorderColor: Record<
  ActionButtonVariant,
  Record<
    'default' | 'hover',
    {
      borderColor: TailwindColor
      color: TailwindColor
      backgroundColor: TailwindColor
    }
  >
> = {
  lightPurple: {
    default: {
      backgroundColor: tailwindColors.primary.light.DEFAULT,
      color: mapVariantToColor.lightPurple,
      borderColor: tailwindColors.primary.light.DEFAULT
    },
    hover: {
      backgroundColor: tailwindColors.primary.DEFAULT,
      color: mapVariantToColor.lightPurple,
      borderColor: tailwindColors.primary.DEFAULT
    }
  },
  primary: {
    default: {
      backgroundColor: tailwindColors.white.DEFAULT,
      color: mapVariantToColor.primary,
      borderColor: tailwindColors.grey.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.primary,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.primary
    }
  },
  danger: {
    default: {
      backgroundColor: tailwindColors.white.DEFAULT,
      color: mapVariantToColor.danger,
      borderColor: tailwindColors.grey.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.danger,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.danger
    }
  },
  grey: {
    default: {
      backgroundColor: tailwindColors.white.DEFAULT,
      color: mapVariantToColor.grey,
      borderColor: tailwindColors.grey.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.grey,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.grey
    }
  },
  ghost: {
    default: {
      backgroundColor: tailwindColors.primary.light.DEFAULT,
      color: mapVariantToColor.primary,
      borderColor: tailwindColors.primary.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.primary,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.primary
    }
  },
  success: {
    default: {
      backgroundColor: tailwindColors.white.DEFAULT,
      color: mapVariantToColor.success,
      borderColor: tailwindColors.grey.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.success,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.success
    }
  },
  lightGreen: {
    default: {
      backgroundColor: tailwindColors.green.light.DEFAULT,
      color: mapVariantToColor.success,
      borderColor: tailwindColors.grey.light.DEFAULT
    },
    hover: {
      backgroundColor: mapVariantToColor.success,
      color: tailwindColors.white.DEFAULT,
      borderColor: mapVariantToColor.success
    }
  }
}

const ActionButton = ({
  isLoading,
  icon,
  variant,
  className,
  disabled,
  size,
  tooltipText,
  type = 'button',
  onClick
}: Props) => {
  const [isHovered, setIsHovered] = useState(false)

  return (
    <TooltipProvider delayDuration={600}>
      <Tooltip open={tooltipText === undefined ? false : undefined}>
        <TooltipTrigger asChild>
          <button
            type={type}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={onClick}
            className={cn(
              'flex items-center justify-center rounded h-10 aspect-square transition-colors duration-200 ease-in-out border ',
              { 'cursor-not-allowed opacity-50': disabled || isLoading },
              { 'w-7 h-7 rounded-sm': size === 'x-small' },
              { 'w-9 h-9': size === 'small' },
              className
            )}
            disabled={disabled || isLoading}
            style={mapVariantToBorderColor[variant][isHovered ? 'hover' : 'default']}
          >
            {isLoading ? (
              <Loader />
            ) : (
              icon?.({
                color: isHovered ? tailwindColors.white.DEFAULT : mapVariantToColor[variant],
                height: '20px',
                width: '20px'
              })
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent
          sideOffset={8}
          align={'center'}
          className={'mobile:max-w-xs p-3 bg-grey rounded'}
        >
          <Typography variant={'label-semibold'} color={tailwindColors.white.DEFAULT}>
            {tooltipText}
          </Typography>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export default ActionButton
